// frontend/src/components/PostIt.js
import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { motion, useMotionValue } from 'framer-motion';
import './PostIt.css';

const clamp = (value, min, max) => Math.max(min, Math.min(value, max));

const SAFE_TOP_MARGIN_PX = 18;

const getSafeTopPx = (parentRect, safeTopNode) => {
  if (!parentRect || !safeTopNode) return 0;

  const safeTopRect = safeTopNode.getBoundingClientRect();
  if (!safeTopRect.height) return 0;

  return clamp(safeTopRect.bottom - parentRect.top + SAFE_TOP_MARGIN_PX, 0, parentRect.height);
};

const getPostItMetrics = (node, parent, safeTopNode, initialX, initialY) => {
  if (!node || !parent) {
    return null;
  }

  const parentRect = parent.getBoundingClientRect();
  const width = node.offsetWidth || node.getBoundingClientRect().width;
  const height = node.offsetHeight || node.getBoundingClientRect().height;

  if (!parentRect.width || !parentRect.height || !width || !height) {
    return null;
  }

  const safeTopPx = getSafeTopPx(parentRect, safeTopNode);
  const maxLeftPx = Math.max(0, parentRect.width - width);
  const maxTopPx = Math.max(0, parentRect.height - height);
  const minTopPx = Math.min(maxTopPx, safeTopPx);
  const maxX = (maxLeftPx / parentRect.width) * 100;
  const minY = (minTopPx / parentRect.height) * 100;
  const maxY = (maxTopPx / parentRect.height) * 100;
  const safeX = clamp(initialX, 0, maxX);
  const safeY = clamp(initialY, minY, maxY);
  const initialLeftPx = (safeX / 100) * parentRect.width;
  const initialTopPx = (safeY / 100) * parentRect.height;

  return {
    constraints: {
      left: -initialLeftPx,
      right: maxLeftPx - initialLeftPx,
      top: minTopPx - initialTopPx,
      bottom: maxTopPx - initialTopPx
    },
    minY,
    maxX,
    maxY,
    safeX,
    safeY
  };
};

const PostIt = ({ task, canvasRef, safeTopRef, onClick, onRemove, onPositionChange }) => {
  const containerRef = useRef(null);
  const isDragging = useRef(false);
  const normalizedInitialPosition = useRef(false);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const [dragMetrics, setDragMetrics] = useState({
    constraints: { left: 0, right: 0, top: 0, bottom: 0 },
    minY: 0,
    maxX: 100,
    maxY: 100,
    safeX: 0,
    safeY: 0
  });

  // Generamos una rotación y posición inicial basada en el ID o en la meta
  const { initialX, initialY, rotation } = useMemo(() => {
    const seed = task.id || 1;
    const rot = (seed * 137.5) % 10 - 5; // Entre -5 y 5 grados

    let x, y;

    if (task.meta?.visual_position) {
      x = task.meta.visual_position.x;
      y = task.meta.visual_position.y;
    } else {
      // Posicionamiento aleatorio determinista inicial
      x = (seed * 41.3) % 70 + 10; // Entre 10% y 80%
      y = (seed * 97.7) % 60 + 10;  // Entre 10% y 70% (ajustado por el nuevo padding-top)
    }

    return { initialX: x, initialY: y, rotation: rot };
  }, [task.id, task.meta?.visual_position]);

  const colorClass = `color-${task.id % 5}`;

  const updateDragMetrics = useCallback(() => {
    const node = containerRef.current;
    const parent = canvasRef?.current || (node ? node.closest('.postits-canvas') : null);
    const safeTopNode = safeTopRef?.current || document.querySelector('.visual-tasks-ui');
    const metrics = getPostItMetrics(node, parent, safeTopNode, initialX, initialY);

    if (metrics) {
      setDragMetrics(prevMetrics => {
        const hasSameConstraints =
          prevMetrics.constraints.left === metrics.constraints.left &&
          prevMetrics.constraints.right === metrics.constraints.right &&
          prevMetrics.constraints.top === metrics.constraints.top &&
          prevMetrics.constraints.bottom === metrics.constraints.bottom;

        if (
          hasSameConstraints &&
          prevMetrics.minY === metrics.minY &&
          prevMetrics.maxX === metrics.maxX &&
          prevMetrics.maxY === metrics.maxY &&
          prevMetrics.safeX === metrics.safeX &&
          prevMetrics.safeY === metrics.safeY
        ) {
          return prevMetrics;
        }

        return {
          constraints: metrics.constraints,
          minY: metrics.minY,
          maxX: metrics.maxX,
          maxY: metrics.maxY,
          safeX: metrics.safeX,
          safeY: metrics.safeY
        };
      });
    }
  }, [canvasRef, safeTopRef, initialX, initialY]);

  useLayoutEffect(() => {
    updateDragMetrics();

    const parent = canvasRef?.current || containerRef.current?.closest('.postits-canvas');
    if (!parent || typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateDragMetrics);
      return () => window.removeEventListener('resize', updateDragMetrics);
    }

    const resizeObserver = new ResizeObserver(updateDragMetrics);
    resizeObserver.observe(parent);

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    if (safeTopRef?.current) {
      resizeObserver.observe(safeTopRef.current);
    }

    window.addEventListener('resize', updateDragMetrics);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateDragMetrics);
    };
  }, [canvasRef, safeTopRef, updateDragMetrics]);

  useLayoutEffect(() => {
    if (!task.meta?.visual_position || normalizedInitialPosition.current || !onPositionChange) return;

    const clampedX = clamp(initialX, 0, dragMetrics.maxX);
    const clampedY = clamp(initialY, dragMetrics.minY, dragMetrics.maxY);
    const movedX = Math.abs(clampedX - initialX) > 0.1;
    const movedY = Math.abs(clampedY - initialY) > 0.1;

    if (!movedX && !movedY) return;

    normalizedInitialPosition.current = true;
    onPositionChange(task.id, clampedX, clampedY, {
      minY: dragMetrics.minY,
      maxX: dragMetrics.maxX,
      maxY: dragMetrics.maxY
    });
  }, [dragMetrics, initialX, initialY, onPositionChange, task.id, task.meta?.visual_position]);

  const safeInitialX = clamp(initialX, 0, dragMetrics.maxX);
  const safeInitialY = clamp(initialY, dragMetrics.minY, dragMetrics.maxY);

  const handleDragEnd = () => {
    const node = containerRef.current;
    const parent = canvasRef?.current || (node ? node.closest('.postits-canvas') : null);
    if (!parent || !node || !onPositionChange) return;

    const parentRect = parent.getBoundingClientRect();
    if (!parentRect.width || !parentRect.height) return;

    const xPercentMoved = (x.get() / parentRect.width) * 100;
    const yPercentMoved = (y.get() / parentRect.height) * 100;
    const finalX = clamp(safeInitialX + xPercentMoved, 0, dragMetrics.maxX);
    const finalY = clamp(safeInitialY + yPercentMoved, dragMetrics.minY, dragMetrics.maxY);

    x.set(0);
    y.set(0);
    onPositionChange(task.id, finalX, finalY, {
      minY: dragMetrics.minY,
      maxX: dragMetrics.maxX,
      maxY: dragMetrics.maxY
    });

    setTimeout(() => {
      isDragging.current = false;
      updateDragMetrics();
    }, 150);
  };

  return (
    <motion.div
      ref={containerRef}
      className={`postit-sticky ${colorClass}`}
      style={{
        x,
        y,
        left: `${safeInitialX}%`,
        top: `${safeInitialY}%`,
        rotate: `${rotation}deg`
      }}
      drag
      dragMomentum={false}
      dragElastic={0}
      dragConstraints={dragMetrics.constraints}
      onDragStart={() => {
        updateDragMetrics();
        isDragging.current = true;
      }}
      onDragEnd={handleDragEnd}
      onClick={(e) => {
        if (isDragging.current) return;
        onClick(e);
      }}
      whileHover={{ scale: 1.05, zIndex: 9999 }}
      whileDrag={{ scale: 1, zIndex: 1000 }}
    >
      <button
        className="postit-remove-btn"
        onClick={(e) => {
          e.stopPropagation();
          onRemove && onRemove(task.id);
        }}
        title="Quitar de este lugar"
      >
        ×
      </button>
      <span className="postit-text">
        {task.main_task}
      </span>
    </motion.div>
  );
};

export default PostIt;
